import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faHeart, faComment, faPaperPlane, faBookmark } from "@fortawesome/free-regular-svg-icons";
import ConsultingHero from "../shared/ConsultingHero/ConsultingHero";
import ServiceIntro from "../shared/ServiceIntro/ServiceIntro";
import ImageContentSection from "../shared/ImageContentSection/ImageContentSection";
import TestimonialsSection from "../shared/TestimonialsSection/TestimonialsSection";
import CTABanner from "../shared/CTABanner/CTABanner";
import AssociatedServices from "../shared/AssociatedServices/AssociatedServices";
import FAQ from "../shared/FAQ/FAQ";
import LinkButton from "../shared/Buttons/LinkButton";
import { marketingWhyItMatters, marketingWhyTrustUs, testimonials, marketingAssociatedServices, marketingFaqs } from "./data/consultingData";

const BASE = "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2";

const ourWorkPosts = [
  {
    images: [
      `${BASE}/post-1-1.png`,
      `${BASE}/post-1-2.png`,
      `${BASE}/post-1-3.png`,
      `${BASE}/post-1-5.png`,
      `${BASE}/post-1-4.png`,
    ],
  },
  {
    images: [
      `${BASE}/post-2-1.png`,
      `${BASE}/post-2-2.png`,
      `${BASE}/post-2-3.png`,
    ],
  },
];

const InstagramCard = ({ images }) => {
  const [index, setIndex] = useState(0);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-md max-w-sm w-full mx-auto border border-gray-200">

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
            <img
              src="/small_logo.png"
              alt="Sponsor a Pet"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-sm font-semibold text-gray-900">sponsorapet</span>
        </div>
        <FontAwesomeIcon icon={faEllipsis} className="text-gray-700 text-sm" />
      </div>

      {/* Image area */}
      <div className="relative aspect-square bg-white">
        <img
          src={images[index]}
          alt={`Post image ${index + 1}`}
          className="w-full h-full object-contain"
        />

        {/* Counter badge */}
        {images.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/50 text-white text-xs font-medium px-2 py-1 rounded-full">
            {index + 1}/{images.length}
          </div>
        )}

        {/* Prev / Next arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center shadow transition-colors"
              aria-label="Previous image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center shadow transition-colors"
              aria-label="Next image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Action bar */}
      <div className="px-3 pt-3 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <FontAwesomeIcon icon={faHeart} className="text-xl text-gray-800 cursor-pointer hover:text-red-500 transition-colors" />
          <FontAwesomeIcon icon={faComment} className="text-xl text-gray-800 cursor-pointer" />
          <FontAwesomeIcon icon={faPaperPlane} className="text-xl text-gray-800 cursor-pointer" />
        </div>
        <FontAwesomeIcon icon={faBookmark} className="text-xl text-gray-800 cursor-pointer" />
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 py-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === index ? "bg-blue" : "bg-gray-300"}`}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Caption */}
      <div className="px-3 pb-4">
        <p className="text-sm text-gray-900">
          <span className="font-semibold">sponsorapet</span>{" "}
          Real stories. Real rescues. Real impact.
        </p>
      </div>

    </div>
  );
};

const MarketingService = () => {
  return (
    <div>
      <ConsultingHero
        title={
          <>
            <span className="block">Grow your</span>
            <span className="block">rescues reach</span>
          </>
        }
        buttonText="Get a free social media strategy call"
        buttonTo="/contact-us"
        bgColor="bg-orange"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/bird-on-jar-480.webp"
        imageSrcSet="
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/bird-on-jar-480.webp 480w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/bird-on-jar-1280.webp 1280w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/bird-on-jar-1920.webp 1920w
        "
        imageAlt="A budgie perched on a jar"
      />

      <ServiceIntro
        title="Grow awareness, trust & donations through smart storytelling"
        description="Reach more adopters, donors, and supporters with inspiring content — and free up your time to focus on saving lives."
        rightCircleSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/right-circles-blue.svg"
      />

      <ImageContentSection
        imagePosition="right"
        imageSrc={marketingWhyItMatters.imageSrc}
        imageAlt={marketingWhyItMatters.imageAlt}
        title={marketingWhyItMatters.title}
        description={marketingWhyItMatters.description}
        items={marketingWhyItMatters.items}
        imageBg="bg-blue"
        imageClassName="md:object-center lg:object-right"
      />

      {/* Pricing plans section */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col items-center text-center mb-12">
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
              alt=""
              className="w-28 md:w-40 mb-4"
            />
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-dark1 mb-6 max-w-2xl">
              Choose the plan that fits your growth goals
            </h2>
            <p className="!text-sm md:!text-base xl:!text-lg leading-relaxed mb-8 max-w-xl">
              No two rescues have the same needs. Whether you're just starting your social media journey or ready to scale your reach, our plans adapt to your team's goals & capacity.
            </p>
            <div className="flex flex-col gap-3 text-left">
              {[
                "Consistent, scheduled posting",
                "Custom graphics and videos for your rescue",
                "Compelling storytelling that drives action",
                "More time for your team to focus on animal care",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/orange-check.svg"
                    alt=""
                    className="w-5 h-5 min-w-[20px]"
                  />
                  <span className="!text-md text-blue-dark1">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              {
                name: "Starter Plan",
                price: "$000",
                bg: "bg-orange",
                textColor: "text-black",
                buttonColour: "white",
                features: ["3 posts/week", "Graphic design", "Basic engagement"],
              },
              {
                name: "Growth Plan",
                price: "$000",
                bg: "bg-blue",
                textColor: "text-white",
                buttonColour: "white",
                features: ["5 posts/week", "Short videos (reels/stories)", "Content calendar"],
              },
              {
                name: "Impact Plan",
                price: "$000",
                bg: "bg-blue-navy",
                textColor: "text-white",
                buttonColour: "white",
                features: ["7 posts/week", "Full content strategy", "Audience growth campaigns"],
              },
            ].map((plan, i) => (
              <div key={i} className="rounded-2xl overflow-hidden flex flex-col">
                <div className={`${plan.bg} p-6 lg:p-8 flex flex-col gap-4 flex-grow`}>
                  <div>
                    <h3 className={`font-bold text-xl lg:text-2xl xl:text-3xl ${plan.textColor} mb-1`}>{plan.name}</h3>
                    <p className={`!text-lg lg:!text-xl font-bold ${plan.textColor}`}>{plan.price}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-grow">
                    {plan.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <img
                          src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/check.svg"
                          alt=""
                          className="w-5 h-5 min-w-[20px]"
                        />
                        <span className={`!text-sm md:!text-base ${plan.textColor}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <LinkButton to="/contact-us" colour={plan.buttonColour} className="mt-4 self-start">
                    Choose plan
                  </LinkButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our work section */}
      <section className="bg-blue py-16 px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center gap-8 mb-10">
            <div className="md:w-1/2 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-blue-navy">
                Our work
              </h2>
            </div>
            <div className="md:w-1/2 text-center md:text-left">
              <p className="!text-sm md:!text-base xl:!text-lg leading-relaxed text-white">
                Take a look at real examples of social media posts we've created for animal nonprofits — focused on adoption, fostering, fundraising, and awareness
              </p>
            </div>
          </div>

          {/* Instagram cards */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            {ourWorkPosts.map((post, i) => (
              <InstagramCard key={i} images={post.images} />
            ))}
          </div>

          {/* Instagram link */}
          <div className="flex justify-center mt-10">
            <a
              href="https://www.instagram.com/sponsorapet/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-navy font-semibold px-6 py-3 rounded-full hover:bg-orange transition-colors"
            >
              <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" />
              View our Instagram
            </a>
          </div>


        </div>
      </section>

      <ImageContentSection
        imagePosition="left"
        imageSrc={marketingWhyTrustUs.imageSrc}
        imageAlt={marketingWhyTrustUs.imageAlt}
        title={marketingWhyTrustUs.title}
        description={marketingWhyTrustUs.description}
        items={marketingWhyTrustUs.items}
        imageBg="bg-blue"
        contentBg=""
      />

      <TestimonialsSection
        bgColor="bg-blue/20"
        cardBg="bg-white"
        showHorizontalLine={false}
        title={
          <>
            We help animal rescues thrive
          </>
        }
        description="Testimonials from rescues/shelters about increased adoptions, fundraising success, or better online visibility."
        testimonials={testimonials}
        images={[
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/green-bird.webp", alt: "A green parrot", bg: "bg-orange" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-bird.webp", alt: "A white budgie", bg: "bg-blue" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/bird-in-hand.webp", alt: "A bird perched on a hand", bg: "bg-blue-navy" },
        ]}
      />
      <CTABanner
        bgColor="bg-orange"
        overlayFrom="from-orange via-orange/80"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/green-bird2.webp"
        imageAlt="A green parrot"
        title="Getting Started"
        description="Let's grow your rescue's reach — together"
        buttonText="Get Started"
        buttonTo="/contact-us"
        buttonColour="blue"
      />

      <AssociatedServices
        subtitle="More ways to grow your audience."
        services={marketingAssociatedServices}
        cols={3}
      />

      <FAQ subtitle="About Social Media Management" faqs={marketingFaqs} />
    </div>
  );
};

export default MarketingService;
